<template>
  <a id="ProductPersonality" />
  <div class="ProductPersonality desktopMaxW-lg grid grid-cols-1 desk:grid-cols-2 gap-48 desk:gap-96 mb-48 desk:mb-96">
    <div class="flex flex-col gap-24">
      <!-- Name & Description -->
      <div class="flex flex-col gap-12">
        <div class="type-headline-xl">{{ $t('personality.headline') }}</div>
        <div class="type-headline-md text-text-subtle">
          {{ activeVariant.variantDescription }}
        </div>
      </div>

      <!-- USP -->
      <div class="flex flex-col gap-8 desk:gap-12 bg-bg-attention p-12 desk:p-16 type-base">
        <div class="type-headline-base">{{ $t('personality.features') }}</div>
        <ul class="flex flex-col gap-8 desk:gap-12">
          <li
            v-for="chip in chips"
            :key="chip.code"
            class="inline-flex items-center gap-8"
          >
            <img
              v-if="chip.image !== ''"
              :src="chip.image"
              :alt="chip.name"
              class="w-20 h-20"
            >
            {{ chip.name }}
          </li>
        </ul>
      </div>
    </div>

    <!-- Stats -->
    <ul class="flex flex-col gap-24 desk:gap-32 text-text-subtle">
      <li
        v-for="parametric in usedParametrics"
        :key="parametric.code"
        class="flex items-center gap-16 desk:gap-20"
      >
        <img
          :src="`/icons/personality/${parametric.code}.svg`"
          :alt="parametric.name"
          class="w-32 h-32 desk:w-40 desk:h-40"
        >
        <div class="grow flex flex-col gap-6">
          <div class="type-tagline">{{ parametric.name }}</div>
          <div class="bg-bg-accenture relative w-full h-2">
            <div
              class="absolute top-half -translate-x-half -translate-y-half w-12 h-12 rounded-full bg-bg-accentureStrong"
              :title="parametric.valueName"
              :style="{
                left: `${parametric.value}%`
              }"
            />
          </div>
          <div class="flex items-center justify-between type-sm gap-12">
            <div>{{ $t(`dyn.personality.${parametric.code}.min`) }}</div>
            <div class="text-right">{{ $t(`dyn.personality.${parametric.code}.max`) }}</div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import type { ProductModel } from '#models/product';
import type { ProductVariantModel } from '#models/productVariant';
import Tooltip from '~/components/body/Tooltip.vue';
import { computed } from 'vue';
import { useGlobalContentStore } from '~/store/globalContent';

const props = defineProps<{
  productItem: ProductModel;
  activeVariant: ProductVariantModel,
}>();

const globalContentStore = useGlobalContentStore();

const usedParametrics = computed(()=> {
  const output = props.activeVariant.parametrics.filter(f => f.groupCode === 'B2CPersonalityValueMatrix').map(m => {
    return {
      name: m.name,
      code: m.code,
      value: m.listValue?.code || null,
      valueName: m.listValue?.name || '',
    };
  });

  const filtered = output.filter(f => f.value !== null);

  // Apply US market ordering
  if (globalContentStore.getCountryCode === 'us') {
    const orderMap: { [key: string]: number } = {
      'VisualFinish': 0,
      'Character': 1,
      'SunExposure': 2,
      'Maintenance': 3,
      'Installation': 4,
    };

    return [...filtered].sort((a, b) => {
      const orderA = orderMap[a.code] ?? 999;
      const orderB = orderMap[b.code] ?? 999;
      return orderA - orderB;
    });
  }

  return filtered;
});

const chips = computed(()=> {
  return props.activeVariant.parametricByCode('B2CChips')?.multipleValues || [];
});

</script>
